// Story 7.3 (ADO-1139) — availability batch-read client (AC1).
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for the
// Availability module's batch read. Mirrors `browse.client.ts` /
// `search.client.ts`: it is the SINGLE consumer seam until the
// `openapi-typescript`-generated client lands, and it is exercised against msw
// in tests. The backend serves this route under the `/api/v1` global prefix
// (ADO-1181), matching this client's `API_PREFIX` paths.
//
// The wire shapes are a LOCK-STEP COPY of the availability module's exported
// `AvailabilityStateDto` (libs/modules/availability contracts/dtos) — keep the
// two in sync. SKUs with no stock record return NO entry: the card renders no
// badge (unknown is never claimed as In Stock).

import { API_PREFIX, apiFetch } from './shoplink-api';

/** Feed-sourced supplier availability (DD-S.2 axis 1) — display only here;
 *  the ordering block itself is enforced API-side (Story 8.2). */
export type SupplierAvailabilityStatus = 'AVAILABLE' | 'UNAVAILABLE';

/** Display band derived SERVER-side (read/emit time) — the client never computes bands. */
export type AvailabilityBand = 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK';

/** One SKU's availability at the session depot (lock-step with AvailabilityStateDto). */
export interface AvailabilityState {
  readonly skuCode: string;
  readonly band: AvailabilityBand;
  readonly status: SupplierAvailabilityStatus;
  readonly quantityOnHand: number;
}

export const AVAILABILITY_PATH = `${API_PREFIX}/availability`;

/** Server-side Zod cap on the `skus` batch (availability-query.validation.ts,
 *  lock-step) — the client chunks larger requests so none can 400 on size. */
export const MAX_AVAILABILITY_BATCH_SIZE = 100;

/**
 * The outcome of one batch read. A read is PER-CHUNK, so it can also be PARTIAL:
 * `states` carries the rows every chunk that succeeded returned, and `unreadSkus`
 * names the SKUs whose chunk did not.
 *
 * The two are reported separately because "absent from `states`" means two different
 * things, and the caller has to tell them apart: a SKU a SUCCESSFUL chunk had no row
 * for is ANSWERED ("not stocked at this depot" — render no badge), while a SKU in
 * `unreadSkus` is UNKNOWN ("we could not ask"). Collapsing them would report an
 * outage as an authoritative empty.
 */
export interface AvailabilityReadResult {
  /** Rows returned by every chunk that succeeded. */
  readonly states: readonly AvailabilityState[];
  /** SKUs whose chunk failed — neither returned nor established as absent. */
  readonly unreadSkus: readonly string[];
}

/**
 * Fetch the availability state for `skuCodes` at `depotId` (AC1). Requests are
 * chunked to the server batch cap and each chunk succeeds or fails ON ITS OWN:
 * one chunk's non-2xx no longer discards another chunk's rows, it just puts its own
 * SKUs in `unreadSkus`. This never rejects — a read that establishes nothing reports
 * every requested SKU as unread instead, so the caller always gets an outcome.
 *
 * Per-chunk isolation matters because callers batch: `useAvailabilityStore` merges
 * every read asked for in one tick into a single `fetchAvailability` call, so a wide
 * listing plus its per-card substitute-candidate reads can span several chunks. An
 * all-or-nothing read made that whole commit ONE failure domain — 100 good rows
 * thrown away, and every caller in the tick told its read failed (7-FU-I review).
 */
export async function fetchAvailability(
  depotId: string,
  skuCodes: readonly string[],
): Promise<AvailabilityReadResult> {
  const unique = [...new Set(skuCodes)].filter((sku) => sku !== '');
  if (unique.length === 0) return { states: [], unreadSkus: [] };

  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += MAX_AVAILABILITY_BATCH_SIZE) {
    chunks.push(unique.slice(i, i + MAX_AVAILABILITY_BATCH_SIZE));
  }

  const results = await Promise.all(
    chunks.map(async (chunk): Promise<AvailabilityReadResult> => {
      try {
        const url = `${AVAILABILITY_PATH}?depotId=${encodeURIComponent(depotId)}&skus=${encodeURIComponent(chunk.join(','))}`;
        const res = await apiFetch(url);
        if (!res.ok) throw new Error(`Failed to load availability (${res.status})`);
        return { states: (await res.json()) as AvailabilityState[], unreadSkus: [] };
      } catch {
        // This chunk established nothing. Its SKUs are UNKNOWN, not absent — the
        // caller must not tombstone them as "no stock row here".
        return { states: [], unreadSkus: chunk };
      }
    }),
  );
  return {
    states: results.flatMap((r) => r.states),
    unreadSkus: results.flatMap((r) => r.unreadSkus),
  };
}
